import { ChangeEvent, CSSProperties, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/utils/classNames.ts';
import Portal from '@/components/shared/Portal/Portal.tsx';
import { useOnClickOutside } from 'usehooks-ts';

type Option<T> = {
  value: T;
  label: string;
};

type SelectInputDropdownProps<T> = {
  options: Option<T>[];
  value: T;
  searchable: boolean;
  searchablePlaceholder: string;
  searchQuery: string;
  onSearchChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onSelect: (value: T) => void;
  onClose: () => void;
  buttonRef: RefObject<HTMLButtonElement | null>;
  name?: string;
};

const DROPDOWN_GAP = 5;
const VIEWPORT_PADDING = 8;

const SelectInputDropdown = <T,>({
  options,
  value,
  searchable,
  searchablePlaceholder,
  searchQuery,
  onSearchChange,
  onSelect,
  onClose,
  buttonRef,
  name,
}: SelectInputDropdownProps<T>) => {
  const dropdownRef = useRef<HTMLUListElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});

  const updateDropdownPosition = useCallback(() => {
    const button = buttonRef.current;
    const dropdown = dropdownRef.current;

    if (!button || !dropdown) return setTimeout(updateDropdownPosition, 10);

    const buttonRect = button.getBoundingClientRect();
    const dropdownHeight = dropdown.offsetHeight;
    const dropdownWidth = buttonRect.width >= 200 ? buttonRect.width : 200;

    const spaceBelow = window.innerHeight - buttonRect.bottom - DROPDOWN_GAP - VIEWPORT_PADDING;
    const spaceAbove = buttonRect.top - DROPDOWN_GAP - VIEWPORT_PADDING;

    const openBelow = spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove;

    let top: number;
    let maxHeight: number;

    if (openBelow) {
      top = buttonRect.bottom + DROPDOWN_GAP;
      maxHeight = Math.min(dropdownHeight, spaceBelow);
    } else {
      maxHeight = Math.min(dropdownHeight, spaceAbove);
      top = buttonRect.top - DROPDOWN_GAP - maxHeight;
    }

    let left = buttonRect.left;
    if (left + dropdownWidth > window.innerWidth - VIEWPORT_PADDING) {
      left = window.innerWidth - VIEWPORT_PADDING - dropdownWidth;
    }
    if (left < VIEWPORT_PADDING) {
      left = VIEWPORT_PADDING;
    }

    setDropdownStyle({
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      width: `${dropdownWidth}px`,
    });
  }, [buttonRef]);

  // Attach resize/scroll listeners on mount, clean up on unmount
  useEffect(() => {
    updateDropdownPosition();
    window.addEventListener('resize', updateDropdownPosition);
    window.addEventListener('scroll', updateDropdownPosition, true);

    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    };
  }, [updateDropdownPosition]);

  useOnClickOutside(dropdownRef, onClose);

  return (
    <Portal elementId="dropdown-wrapper">
      <ul
        ref={dropdownRef}
        tabIndex={0}
        style={dropdownStyle}
        className={cn('menu bg-base-100 rounded-box z-10 shadow-sm overflow-y-auto flex-nowrap max-h-[250px]', {
          'p-2': !searchable,
          'px-2 pt-0 pb-2': searchable,
        })}
      >
        {searchable ? (
          <li className="sticky top-0 py-2 z-10 bg-base-100">
            <input
              placeholder={searchablePlaceholder}
              className="bg-transparent border input-bordered cursor-text max-w-full"
              type="text"
              value={searchQuery}
              onChange={onSearchChange}
            />
          </li>
        ) : null}
        {options.map((option) => (
          <li key={`${option.label}_${name ?? ''}_${option.value}`}>
            <button
              type="button"
              className={cn(
                'active:bg-primary active:text-primary-content hover:bg-primary hover:bg-opacity-20 focus:bg-primary focus:bg-opacity-20',
                {
                  '!bg-primary !text-primary-content': option.value === value,
                }
              )}
              onClick={() => onSelect(option.value)}
            >
              {option.label}
            </button>
          </li>
        ))}
      </ul>
    </Portal>
  );
};

export default SelectInputDropdown;
